import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Lightbulb, ChevronRight, Code2, Target, Clock } from 'lucide-react';
import Header from './shared/Header';
import Footer from './shared/Footer';

const AboutPage = () => {
  const approach = [
    {
      icon: <Code2 className="w-6 h-6" />,
      title: 'We ship code, not slide decks',
      description:
        "Every engagement ends with working software. We don't deliver recommendations. We deliver the thing.",
      color: 'blue',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Small team, senior operators',
      description:
        'No junior staff learning on your budget. Every person on your engagement has shipped this kind of work before.',
      color: 'blue',
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Outcomes over hours',
      description:
        "We're not billing by the hour hoping you renew. We scope to a defined outcome and hit it.",
      color: 'purple',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'We stay until it works',
      description:
        "Handoff doesn't mean disappear. We stay involved through go-live and beyond.",
      color: 'green',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            About
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            We build things that work.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            DataBackfill is a studio for teams that need AI and data systems shipped, operated, and trusted in production.
          </p>
        </div>
      </section>

      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Mission Statement */}
          <div className="max-w-3xl mx-auto text-center mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Mission</h2>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                DataBackfill is a boutique AI and data delivery studio. We build Claude-powered
                solutions, modernize data stacks, and ship production software for companies that
                need results, not more meetings.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We believe the best consulting leaves clients with something real: a system running
                in production, a team that knows how to operate it, and the confidence that comes
                from working with people who've done this before.
              </p>
            </div>
          </div>

          {/* Our Approach */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-4 text-center">Our approach</h2>
            <p className="text-gray-600 text-center max-w-xl mx-auto mb-12">
              A few principles that shape how we work with every client.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {approach.map((item, index) => (
                <div key={index} className="rounded-2xl border border-gray-200 p-6">
                  <div className={`w-12 h-12 bg-${item.color}-50 rounded-xl flex items-center justify-center mb-6`}>
                    <div className={`text-${item.color}-600`}>{item.icon}</div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                  <Users className="text-blue-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">User-Centric Design</h3>
                <p className="text-gray-600">
                  Prioritize ease of use and focus on building tools that are both powerful
                  and user-friendly.
                </p>
              </div>

              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-6">
                  <Shield className="text-purple-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Security First</h3>
                <p className="text-gray-600">
                  Your data is valuable. We adhere to the highest standards of data
                  protection and privacy.
                </p>
              </div>

              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-6">
                  <Lightbulb className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Innovation</h3>
                <p className="text-gray-600">
                  We are committed to continual improvement and staying ahead of emerging trends
                  in data analytics and integration.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <section className="py-12 md:py-20">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-blue-600 rounded-3xl p-6 md:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full transform translate-x-1/2 -translate-y-1/2 blur-2xl" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-700 rounded-full transform -translate-x-1/2 translate-y-1/2 blur-2xl" />
                <div className="relative max-w-lg mx-auto text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                    Ready to work together?
                  </h2>
                  <p className="text-lg text-blue-100 mb-8">
                    Tell us what you're trying to build. If it's the right fit, we'll scope it and ship it.
                  </p>
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-white text-blue-600
                             rounded-xl text-base md:text-lg font-semibold hover:bg-blue-50 transition-all
                             shadow-lg hover:shadow-xl hover:translate-y-[-1px]"
                  >
                    Start a Project
                    <ChevronRight className="ml-2" size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AboutPage;
